import { AGENT_QUERIES_BASE_KEY } from '@/api/agents/queries/get-agent-query'
import { IApi } from '@/setup/api-client'
import { useApiQuery } from '@/utils/api/api-query'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'

export interface IBrandManagerAgentResponse {
    agentUuid: string | null
}

export interface IGetBrandManagerAgentQueryParams {
    organizationId: string
    brandSlug: string
}

export const BRAND_MANAGER_AGENT_QUERY_BASE_KEY = [...AGENT_QUERIES_BASE_KEY, 'brand-manager-agent']

export class GetBrandManagerAgentQuery {
    static getKey(params: IGetBrandManagerAgentQueryParams) {
        return [...BRAND_MANAGER_AGENT_QUERY_BASE_KEY, params.organizationId, params.brandSlug]
    }

    static async call(api: IApi, params: IGetBrandManagerAgentQueryParams) {
        return api
            .get<IBrandManagerAgentResponse>(
                `/organization/${params.organizationId}/brands/${params.brandSlug}/manager-agent`
            )
            .then((res) => res?.data)
    }
}

export function useGetBrandManagerAgentQuery(options?: { enabled?: boolean; selfHealing?: boolean }) {
    const { api, apiParams } = useApiToolbox()
    const organizationId = apiParams.organizationId ?? ''
    const brandSlug = apiParams.brandSlug

    const params: IGetBrandManagerAgentQueryParams = {
        organizationId,
        brandSlug,
    }

    return useApiQuery({
        queryKey: GetBrandManagerAgentQuery.getKey(params),
        queryFn: () => GetBrandManagerAgentQuery.call(api, params),
        enabled: !!organizationId && !!brandSlug && (options?.enabled ?? true),
        // `selfHealing` is for surfaces where this uuid is the SOLE binding to the whole manager
        // layer (the dashboard ultra binding): under the wrapper's `retry: false` default, one
        // failed GET would silently strand that layer unbound — every seat idle, no error surfaced,
        // and nothing left to trigger a refetch. Opt-in only: the sidebar's recent-tasks filter also
        // mounts this query for every user, and a blanket error-poll there would hammer a failing
        // route app-wide.
        ...(options?.selfHealing
            ? {
                  retry: 3,
                  refetchInterval: (query: { state: { status: string } }) =>
                      query.state.status === 'error' ? 15_000 : false,
              }
            : {}),
    })
}
